import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Turnstile } from '@marsidev/react-turnstile';
import { useSupabaseAuth } from '../contexts/SupabaseAuthContext';
import { supabase } from '../supabase/supabase';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMail, FiLock, FiLoader, FiAlertCircle, FiCheckCircle, FiArrowLeft, FiInfo, FiShield } = FiIcons;

const RegisterPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const isSellerParam = params.get('role') === 'seller';
  
  const fromPath = location.state?.from || '/';
  const isFromListing = fromPath.includes('/seller/');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    location: '',
    isSeller: isSellerParam
  });

  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isRegistered, setIsRegistered] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileKey, setTurnstileKey] = useState(0);

  const { user, signUp, loading: authLoading } = useSupabaseAuth();
  const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY;

  useEffect(()=> {
    if (user && !authLoading) {
      navigate(fromPath, { replace: true });
    }
  }, [user, authLoading, navigate, fromPath]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!turnstileToken) {
      setError({ message: 'Please complete the security verification.' });
      return;
    }
    if (!agreedToTerms) {
      setError({ message: 'You must agree to the Terms and Conditions and Privacy Policy.' });
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError({ message: 'Passwords do not match.' });
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error: signUpError } = await signUp(formData.email, formData.password, {
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        location: formData.location.trim(),
        isSeller: formData.isSeller
      }, turnstileToken);
      if (signUpError) throw signUpError;
      setIsRegistered(!data?.session);
    } catch (err) {
      console.error('Registration Error:', err);
      setError({ message: err.message || 'Registration failed.' });
    } finally {
      setLoading(false);
      setTurnstileToken(null);
      setTurnstileKey((currentKey) => currentKey + 1);
    }
  };

  const handleResendConfirmation = async () => {
    if (!turnstileToken) {
      setError({ message: 'Please complete the security verification.' });
      return;
    }

    setLoading(true);
    setError(null);
    setResendMessage('');
    try {
      const { error: resendError } = await supabase.auth.resend({
        type: 'signup',
        email: formData.email,
        options: {
          emailRedirectTo: window.location.origin,
          captchaToken: turnstileToken
        }
      });
      if (resendError) throw resendError;
      setResendMessage('If this address is eligible for confirmation, Supabase will send an email. If nothing arrives, check your spam folder and the project email settings.');
    } catch (err) {
      console.error('Confirmation resend error:', err);
      setError({ message: err.message || 'Unable to request a confirmation email.' });
    } finally {
      setLoading(false);
      setTurnstileToken(null);
      setTurnstileKey((currentKey) => currentKey + 1);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  if (isRegistered) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center bg-slate-50 py-12 px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="max-w-md w-full bg-white p-10 rounded-[3rem] shadow-2xl text-center border border-slate-100">
          <div className="w-20 h-20 bg-indigo-50 rounded-3xl flex items-center justify-center mx-auto mb-8">
            <SafeIcon icon={FiCheckCircle} className="text-4xl text-indigo-600" />
          </div>
          <h2 className="text-3xl font-black text-slate-900 mb-4 tracking-tight">Check Your Inbox</h2>
          <p className="text-slate-500 mb-2 leading-relaxed font-medium">
            Supabase has not signed you in yet, so email confirmation may be required for <span className="font-bold text-indigo-600">{formData.email}</span>.
          </p>
          <p className="text-slate-500 mb-6 leading-relaxed font-medium">
            If no email arrives, check your spam folder and ask the site administrator to verify that email confirmation and email delivery are configured in Supabase.
          </p>
          {error && (
            <div className="bg-red-50 border border-red-100 text-red-700 px-4 py-3 rounded-2xl flex items-start text-sm mb-4 text-left">
              <SafeIcon icon={FiAlertCircle} className="mt-0.5 mr-2 flex-shrink-0" />
              <span className="font-bold">{error.message}</span>
            </div>
          )}
          {resendMessage && (
            <p className="text-sm text-indigo-700 mb-4 leading-relaxed">{resendMessage}</p>
          )}
          <div className="flex justify-center mb-4">
            <Turnstile key={turnstileKey} siteKey={SITE_KEY} onSuccess={(token) => setTurnstileToken(token)} onExpire={() => setTurnstileToken(null)} />
          </div>
          <button
            type="button"
            onClick={handleResendConfirmation}
            disabled={loading || !turnstileToken}
            className="block w-full py-4 bg-indigo-600 text-white font-black rounded-2xl hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100 disabled:opacity-50"
          >
            {loading ? <SafeIcon icon={FiLoader} className="animate-spin" /> : 'Resend Confirmation Email'}
          </button>
          <Link to="/login" className="block w-full py-4 mt-3 text-indigo-600 font-black rounded-2xl hover:bg-indigo-50 transition-all">
            Go to Login
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-64px)] flex items-center justify-center bg-slate-50 py-12 px-4 sm:px-6 lg:px-8">
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        className="max-w-xl w-full space-y-8 bg-white p-8 md:p-10 rounded-[3rem] shadow-2xl border border-slate-100"
      >
        <div className="text-center">
          <Link to={fromPath} className="inline-flex items-center text-[10px] font-black text-slate-400 hover:text-indigo-600 uppercase tracking-widest mb-8 transition-colors group">
            <SafeIcon icon={FiArrowLeft} className="mr-2 group-hover:-translate-x-1 transition-transform" /> 
            {isFromListing ? 'Return to Listing' : 'Back to Marketplace'}
          </Link>

          <div className="w-20 h-20 bg-slate-900 rounded-3xl flex items-center justify-center p-4 mx-auto mb-6">
            <img src="https://assets.sohlocal.com/Branding/Icon.png" alt="SohLocal" className="w-full h-full object-contain" />
          </div>
          <h2 className="text-3xl font-black text-slate-900 tracking-tight">Create Your Account</h2>
          <p className="mt-2 text-sm text-slate-500 font-medium">Connect with local bakers in your neighborhood</p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 border border-red-100 text-red-700 px-4 py-3 rounded-2xl flex items-start text-sm">
              <SafeIcon icon={FiAlertCircle} className="mt-0.5 mr-2 flex-shrink-0" />
              <span className="font-bold">{error.message}</span>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-2 ml-1">Full Name</label>
              <input name="name" type="text" required value={formData.name} onChange={handleChange} className="block w-full px-4 py-3.5 bg-slate-50 border border-slate-100 rounded-2xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all font-medium" placeholder="Sarah Jenkins" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-2 ml-1">Email Address</label>
              <input name="email" type="email" required value={formData.email} onChange={handleChange} className="block w-full px-4 py-3.5 bg-slate-50 border border-slate-100 rounded-2xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all font-medium" placeholder="sarah@example.com" />
            </div>
            <div>
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-2 ml-1">Password</label>
              <input name="password" type="password" required value={formData.password} onChange={handleChange} className="block w-full px-4 py-3.5 bg-slate-50 border border-slate-100 rounded-2xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all font-medium" placeholder="Min 6 chars" />
            </div>
            <div>
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-2 ml-1">Confirm</label>
              <input name="confirmPassword" type="password" required value={formData.confirmPassword} onChange={handleChange} className="block w-full px-4 py-3.5 bg-slate-50 border border-slate-100 rounded-2xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all font-medium" placeholder="Repeat password" />
            </div>
          </div>

          <div className="space-y-4">
            {/* Baker Disclaimer */}
            <div className="p-6 bg-slate-50 rounded-3xl border border-slate-100">
              <div className="flex items-center mb-3">
                <input id="isSeller" name="isSeller" type="checkbox" checked={formData.isSeller} onChange={handleChange} className="h-5 w-5 text-indigo-600 focus:ring-indigo-500 border-slate-300 rounded-lg cursor-pointer" />
                <label htmlFor="isSeller" className="ml-3 block text-sm font-black text-slate-700 cursor-pointer uppercase tracking-tight"> I want to become a local seller </label>
              </div>
              {formData.isSeller && (
                <div className="pl-8 text-[10px] leading-relaxed text-slate-500 font-medium italic">
                  <span className="font-black text-indigo-600 uppercase tracking-widest block mb-1">Baker Responsibility Agreement</span>
                  I confirm that I am solely responsible for complying with all local food safety, licensing, tax, labeling, allergy, refund, and consumer protection laws. I understand that SOHLocal does not process payments, fulfill orders, inspect my kitchen, verify my legal compliance, or take responsibility for my products or services.
                </div>
              )}
            </div>

            {/* Buyer Disclaimer */}
            <div className="p-5 bg-indigo-50/50 rounded-2xl border border-indigo-100/50 flex items-start">
              <SafeIcon icon={FiInfo} className="mt-0.5 mr-3 text-indigo-600 flex-shrink-0" />
              <div className="text-[10px] leading-relaxed text-indigo-900/70 font-medium">
                <span className="font-black uppercase tracking-widest text-indigo-600 block mb-0.5">Buyer Notice</span>
                I understand that SOHLocal only connects me with independent bakers. Payments, orders, refunds, pickup, delivery, ingredients, allergens, food safety, and fulfillment are handled directly with the baker.
              </div>
            </div>

            {/* General Terms Agreement */}
            <div className="flex items-start p-4 bg-slate-50 rounded-2xl border border-slate-100">
              <div className="flex items-center h-5">
                <input 
                  id="agreedToTerms" 
                  name="agreedToTerms" 
                  type="checkbox" 
                  checked={agreedToTerms} 
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  required
                  className="h-5 w-5 text-indigo-600 focus:ring-indigo-500 border-slate-300 rounded-lg cursor-pointer" 
                />
              </div>
              <label htmlFor="agreedToTerms" className="ml-3 block text-[10px] font-black text-slate-600 cursor-pointer uppercase tracking-widest leading-normal">
                I agree to the 
                <Link to="/terms" target="_blank" className="text-indigo-600 hover:underline mx-1">Terms and Conditions</Link> 
                and 
                <Link to="/privacy" target="_blank" className="text-indigo-600 hover:underline mx-1">Privacy Policy</Link>
              </label>
            </div>
          </div>

          <div className="flex justify-center">
            <Turnstile key={turnstileKey} siteKey={SITE_KEY} onSuccess={(token) => setTurnstileToken(token)} onExpire={() => setTurnstileToken(null)} />
          </div>

          <button 
            type="submit" 
            disabled={loading || !turnstileToken || !agreedToTerms} 
            className="w-full py-4 bg-indigo-600 text-white font-black rounded-2xl hover:bg-indigo-700 transition-all shadow-xl shadow-indigo-100 disabled:opacity-50 disabled:grayscale transform active:scale-[0.98]" 
          >
            {loading ? <SafeIcon icon={FiLoader} className="animate-spin" /> : 'Create Account'}
          </button>

          <div className="text-center pt-2">
            <span className="text-xs text-slate-500 font-bold uppercase tracking-widest">
              Already have an account?{' '}
              <Link to="/login" state={{ from: fromPath }} className="ml-1 text-indigo-600 hover:text-indigo-700 transition-colors">Sign In</Link>
            </span>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default RegisterPage;
